import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnInit, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_LUXON_DATE_FORMATS, MatLuxonDateModule } from '@angular/material-luxon-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatPaginator } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { DateTime } from 'luxon';
import * as XLSX from 'xlsx';
import { InvoiceStatus, KeyLabelPair, MY_DATE_FORMATS, PaymentDetails, PaymentMode, Registration, RegistrationCreateRequest, RegistrationUpdateRequest, invoiceStatuses, paymentModes } from '../../../domain/registrator';
import { normalizeDate } from '../../../domain/utils';
import { AttendeeListFormatterPipe } from '../../../pipes/attendee-list-formatter.pipe';
import { EnumLabelPipe } from '../../../pipes/enum-label.pipe';
import { InvoiceListFormatterPipe } from '../../../pipes/invoice-list-formatter.pipe';
import { InvoiceService } from '../../../services/invoice.service';
import { PaymentService } from '../../../services/payment.service';
import { RegistrationService } from '../../../services/registration.service';
import { ConfirmDialogComponent } from '../../misc/confirm-dialog/confirm-dialog.component';
import { PaymentConfirmComponent } from '../payment-confirm/payment-confirm.component';
import { RegistrationAddComponent } from '../registration-add/registration-add.component';
import { RegistrationEditComponent } from '../registration-edit/registration-edit.component';
import { AuthnService } from '../../../services/authn.service';
import { MessageService } from '../../../services/message.service';

@Component({
  selector: 'app-event-registrations',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatRadioModule,
    MatTableModule,
    MatCardModule,
    MatListModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatPaginator,
    MatTabsModule,
    MatDatepickerModule,
    MatLuxonDateModule,
    MatSortModule,
    AttendeeListFormatterPipe,
    InvoiceListFormatterPipe,
    MatProgressSpinnerModule,
    EnumLabelPipe,
  ],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MAT_LUXON_DATE_FORMATS },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MY_DATE_FORMATS,
    },
  ],
  templateUrl: './event-registrations.component.html',
  styleUrl: './event-registrations.component.scss'
})
export class EventRegistrationsComponent implements OnInit, AfterViewInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly registrationService = inject(RegistrationService);
  private readonly paymentService = inject(PaymentService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly messageService = inject(MessageService);
  private readonly authnService = inject(AuthnService);
  private readonly dialog = inject(MatDialog);

  private readonly route = inject(ActivatedRoute);
  event = this.route.parent!.snapshot.data['event'];

  public paymentOptions: KeyLabelPair<keyof typeof PaymentMode>[] = paymentModes;
  public invoiceStatuses: KeyLabelPair<keyof typeof InvoiceStatus>[] = invoiceStatuses;

  loading = signal<boolean>(false);

  registrationsListItems: Registration[] = [];
  registrationsDataSource: MatTableDataSource<Registration> = new MatTableDataSource();
  filterForm: FormGroup;
  pageSize: number = 10;
  pageIndex: number = 0;
  showFirstLastButtons: boolean = true;
  pageSizeOptions = [5, 10, 25, 100];
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  expandedElement: Registration | null = null;
  // eventName: string = '';

  columnsToDisplay = ['id', 'seqNum', 'date', 'customerName', 'contactName', 'contactEmail',
    'country', 'fee', 'paymentMode', 'invoiceNumber', 'status', 'actions'];

  constructor() {

    this.filterForm = this.formBuilder.group({
      year: ['',],
      partnerCode: ['all',],
      countryCode: ['all',],
      programCode: ['all',],
      statusCode: ['all',],
      dossierCode: ['',],
      nom: ['',]
    });
  }

  loadRegistrations() {
    this.registrationService.getEventRegistrations(this.event.shortName, undefined).subscribe({
      next: (list) => {
        this.registrationsListItems = list;
        this.registrationsDataSource.data = this.registrationsListItems;

        let s = sessionStorage.getItem('registrationsListPageSize');
        if (s) {
          let page = parseInt(s);
          if (!isNaN(page)) {
            if (this.pageSizeOptions.includes(page)) {
              // console.log(`setting pagesize to ${s}`);
              this.pageSize = page;
            }
          }
        }
        s = sessionStorage.getItem('registrationsListCurrentPage');
        if (s) {
          let page = parseInt(s);
          if (!isNaN(page)) {
            if ((page > 0) && (page < Math.ceil(this.registrationsListItems.length / this.pageSize))) {
              // console.log(`jumping to page ${s}`);
              this.pageIndex = page;
            }
          }
        }
      },
      error: (error) => {
        console.log(error);
        if (error?.error?.message) {
          console.error('Error message:', error.error.message);
          this.messageService.showMessage('Error loading registrations' + error.error.message.toLowerCase());
        } else {
          this.messageService.showMessage('Error loading registrations');
        }
      }
    });
  }

  ngOnInit() {
    this.loadRegistrations();
  }


  ngAfterViewInit() {
    this.registrationsDataSource.paginator = this.paginator;
    this.registrationsDataSource.sort = this.sort;
    this.sort!.active = 'seqNum';  // Default sorting column
    this.sort!.direction = 'asc'; // Default order (ascending)        
    this.registrationsDataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'customerName':
          return item.customer?.name.toLowerCase() ?? 'N/A'; // Ensure case-insensitive sorting
        case 'contactName':
          return item.customer?.contactName.toLowerCase() ?? 'N/A'; // Ensure case-insensitive sorting
        case 'contactEmail':
          return item.customer?.email.toLowerCase() ?? 'N/A'; // Ensure case-insensitive sorting
        case 'country':
          return item.customer?.country.enName.toLowerCase() ?? 'N/A'; // Ensure case-insensitive sorting          
        case 'fee':
          return item.fee?.target.toLowerCase() ?? 'N/A';
        case 'status':
          return item.invoice?.status.toLowerCase() ?? 'N/A';
        default:
          return (item as Record<string, any>)[property];
      }
    };
  }

  exportRegistrations() {
    throw new Error('Method not implemented.');
  }

  doNothing(_t117: any) {
    throw new Error('Method not implemented.');
  }

  markAsPaid(r: Registration) {
    console.debug('markAsPaid ' + r.id);
    const dialogRef = this.dialog.open(PaymentConfirmComponent, {
      width: '400px', // Set a width for the dialog
      minWidth: '400px',
      data: { eventName: this.event.shortName, registration: r } // Pass data using the 'data' property
    });
    dialogRef.afterClosed().subscribe(result => {
      console.debug('The dialog was closed with ', result);
      if (result) {
        this.loading.set(true);

        const payment: PaymentDetails = {
          registrationId: r.id,
          paymentDate: result.date ? result.date.toISODate() : DateTime.now().toISODate(),
          pfInvoiceNumber: '',
          paymentAmount: 0,
          paymentCurrency: '',
          paymentMethod: '',
          paymentReference: result.reference ?? ''
        };
        this.paymentService.recordRegistrationPayment(payment).subscribe({
          next: (response) => {
            this.messageService.showMessage('Payment recorded successfully.', true);
            this.loadRegistrations();
            this.loading.set(false);
          },
          error: (error) => {
            this.loading.set(false);
            if (error.error && typeof error.error === 'object') {
              console.error('Error message:', error.error.message);
              this.messageService.showMessage('Error recording payment: ' + error.error.message.toLowerCase());
            } else {
              // Handle unexpected response formats
              console.error('Unexpected error format:', error);
              this.messageService.showMessage('An unexpected error occurred.');
            }
          }
        });
      } else {
        console.debug('Dialog cancelled or closed without saving.');
      }
    });
  }

  addRegistration() {
    const dialogRef = this.dialog.open(RegistrationAddComponent, {
      width: '800px', // Set a width for the dialog
      minWidth: '800px',
      data: { eventName: this.event.shortName, registration: null } // Pass data using the 'data' property
    });

    dialogRef.afterClosed().subscribe(result => {
      console.debug('The dialog was closed with ', result);
      if (result) {
        this.loading.set(true);
        const request: RegistrationCreateRequest = { ...result, eventShortName: this.event.shortName };
        let rawValue = request.date;
        let isoDate = normalizeDate(rawValue);
        console.debug('v', rawValue, isoDate);
        if (!isoDate) {
          this.messageService.showMessage('⚠️ Invalid event date');
          return;
        }
        request.date = isoDate
        this.registrationService.createRegistration(request).subscribe(
          {
            next: () => {
              this.loading.set(false);
              this.messageService.showMessage('Registration created successfully.', true);
              this.loadRegistrations();
            },
            error: (error) => {
              this.loading.set(false);
              if (error.error && typeof error.error === 'object') {
                console.error('Error message:', error.error.message);
                this.messageService.showMessage('Error creating registration: ' + error.error.message.toLowerCase());
              } else {
                // Handle unexpected response formats
                console.error('Unexpected error format:', error.error);
                this.messageService.showMessage('An unexpected error occurred.');
              }
            }
          }
        );
      } else {
        console.log('Dialog cancelled or closed without saving.');
      }
    });
  }

  editRegistration(r: Registration) {
    const dialogRef = this.dialog.open(RegistrationEditComponent, {
      width: '800px', // Set a width for the dialog
      minWidth: '800px',
      data: { eventName: this.event.shortName, registration: r } // Pass data using the 'data' property
    });

    dialogRef.afterClosed().subscribe(result => {
      console.debug('The dialog was closed with ', result);
      if (result) {
        this.loading.set(true);
        //console.debug('Form data received:', result);
        // Handle the saved registration data here (e.g., send to backend)
        let rur: RegistrationUpdateRequest;
        rur = {
          id: r.id,
          date: DateTime.fromISO(result.registrationDate).toISODate()!,
          organisation: result.organisation,
          paymentMode: result.paymentMode,
          customer: {
            id: 0,
            name: result.invoice.customerName,
            vatNumber: result.invoice.vatNumber,
            address1: result.invoice.address1,
            address2: result.invoice.address2,
            zip: result.invoice.zip,
            city: result.invoice.city,
            country: result.invoice.country,
            email: result.invoice.contactEmail,
            contactName: result.invoice.contactName
          },
          promoCodeId: (result.promocode ? result.promocode : null),
          feeId: result.fee.id,
          invoiceNumber: result.invoice.invoiceNumber,
          invoiceDate: DateTime.fromISO(result.invoice.invoiceDate).toISODate()!,
          invoiceStatus: result.invoice.invoiceStatus,
          poNumber: result.invoice.poNumber,
          attendees: result.attendees
        }
        console.debug('Registration update request:', rur);

        this.registrationService.updateRegistration(rur).subscribe(
          {
            next: () => {
              this.loading.set(false);
              this.messageService.showMessage('Registration updated successfully.', true);
              this.loadRegistrations();
            },
            error: (error) => {
              this.loading.set(false);
              if (error.error && typeof error.error === 'object') {
                console.error('Error message:', error.error.message);
                this.messageService.showMessage('Error updating registration: ' + error.error.message.toLowerCase());
              } else {
                // Handle unexpected response formats
                console.error('Unexpected error format:', error.error);
                this.messageService.showMessage('An unexpected error occurred.');
              }
            }
          }
        );
      } else {
        console.log('Dialog cancelled or closed without saving.');
      }
    });
  }

  generateInvoice(r: Registration) {
    console.debug('generateInvoice ' + r.invoice);
    if ((!r.invoice) || (!r.invoice.number)) {
      this.messageService.showMessage('No invoice exists for this registration');
      return;
    }
    let invoiceNumber = r.invoice.number;

    this.loading.set(true);
    this.invoiceService.getPDFInvoice(invoiceNumber.toString()).subscribe({
      next: (response) => {
        this.loading.set(false);
        // Create a Blob object from the response
        const blob = new Blob([response], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        // Download or display the PDF
        const a = document.createElement('a');
        a.href = url;
        a.download = invoiceNumber + '.pdf';
        a.click();

        window.URL.revokeObjectURL(url); // Clean up
      },
      error: (error) => {
        this.loading.set(false);
        if (error.error && typeof error.error === 'object') {
          console.error('Error message:', error.error.message);
          this.messageService.showMessage('Error generating invoice: ' + error.error.message.toLowerCase());
        } else {
          // Handle unexpected response formats
          console.error('Unexpected error format:', error.error);
          this.messageService.showMessage('An unexpected error occurred.');
        }
      }
    })
  }

  sendReminderEmail(r: Registration) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      message: 'Are you sure you want to send a reminder email?',
      buttonText: {
        ok: 'Yes',
        cancel: 'Cancel'
      }
    };
    dialogConfig.height = '200px';
    dialogConfig.width = '600px';
    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.loading.set(true);
        console.debug('sendReminderEmail ' + r.id);
        this.paymentService.sendPaymentReminder(r.id).subscribe({
          next: (result) => {
            this.registrationService.getEventRegistrations(this.event!.shortName, undefined).subscribe({
              next: (list) => {
                this.loading.set(false);
                this.registrationsListItems = list;
                this.registrationsDataSource.data = this.registrationsListItems;
                this.ngAfterViewInit();
                this.messageService.showMessage('Reminder email sent to ' + r.customer?.email);
              },
              error: (err) => {
                this.loading.set(false);
              }
            })
          },
          error: (error) => {
            this.loading.set(false);
            this.messageService.showMessage(error)
          }
        });
      }
    })
  }

  deleteRegistration(r: Registration) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      message: 'Are you sure you want to delete this registration and related data?',
      buttonText: {
        ok: 'Yes',
        cancel: 'Cancel'
      }
    };
    dialogConfig.height = '200px';
    dialogConfig.width = '600px';
    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.loading.set(true);
        console.log('deleting registration ', r.id);
        this.registrationService.deleteRegistration(r.id).subscribe({
          next: (result) => {
            this.registrationService.getEventRegistrations(this.event!.shortName, undefined).subscribe({
              next: (list) => {
                this.loading.set(false);
                this.registrationsListItems = list;
                this.registrationsDataSource.data = this.registrationsListItems;
                this.ngAfterViewInit();
                this.messageService.showMessage('Registration deleted');
              },
              error: (err) => {
                this.loading.set(false);
              }
            })
          },
          error: (error) => {
            this.loading.set(false);
            this.messageService.showMessage(error)
          }
        });
      }
    })
  }

  get isAdmin() {
    return (this.authnService.isAuthenticated && (this.authnService.roles!.indexOf('ROLE_ADMIN') > -1));
  }


  exportAttendees(): void {
    const data: any[] = [];
    // this.dataSource.filteredData.forEach(d => {
    this.registrationsDataSource.sortData(this.registrationsDataSource.filteredData, this.registrationsDataSource.sort!).forEach(d => {
      data.push(d);
    });

    // console.log(data);
    const wb = XLSX.utils.book_new();
    if (!wb.Props) wb.Props = {};
    wb.Props.Title = 'Registration list';

    const ws = XLSX.utils.json_to_sheet(data, { cellDates: true, dateNF: 'dd/mm/YYYY' });
    const range = XLSX.utils.decode_range(ws['!ref']!);
    var col_index = XLSX.utils.decode_col("W");
    // console.log(range);
    for (let row = range.s.r + 1; row <= range.e.r; ++row) {
      const ref = XLSX.utils.encode_cell({ r: row, c: col_index })
      if (ws[ref] && ws[ref].t === 'd') {
        ws[ref].z = 'mm/YYYY';
      }
    }
    XLSX.utils.book_append_sheet(wb, ws, "Registrations");
    XLSX.writeFileXLSX(wb, "registrations.xlsx");
  }

}

